import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { PaneDescription as BasePaneDescription } from "./analytics.pane.styles";

import { AddSection as BaseAddSection } from "./analytics.form.styles";

export {
  Backdrop,
  Pane,
  PaneHeader,
  PaneTitle,
  CloseBtn,
  PaneFooter,
  SaveErrorText,
} from "./analytics.pane.styles";

/** Adds breathing room below the description, before the scrollable content
 * (list / add-form) starts — the shared PaneDescription has no bottom margin. */
export const PaneDescription = styled(BasePaneDescription)`
  margin-bottom: 16px;
`;

export {
  EmptyNote,
  DeleteBtn,
  AddSectionTitle,
  FormField,
  FormLabel,
  FormInput,
  FormRow,
  DatePickerWrapper,
  AddButtonRow,
} from "./analytics.form.styles";

export const AddSection = BaseAddSection;

/** Week view stretches the add-form to fill whatever's left of ScrollArea,
 * so the day grid can grow into the remaining space instead of leaving a
 * gap above the footer. */
export const WeekAddSection = styled(BaseAddSection)`
  flex: 1;
  min-height: 0;
`;

/**
 * Scrollable region between the pane header and footer. Keeps Save/Cancel
 * always reachable — without this, a tall sales list plus (in week view)
 * the 7-row grid can push those buttons below the pane's fixed viewport
 * height with no way to scroll down to them.
 */
export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

/** `$height` mirrors the drag-to-resize state: null lets flex decide the
 * height (the default); a number pins it to that height in pixels. */
export const SalesList = styled.div<{ $height?: number | null }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 24px;
  overflow-y: auto;
  min-height: 60px;
  ${({ $height }) =>
    $height != null ? `flex: 0 0 auto; height: ${$height}px;` : `flex: 1;`}
`;

/**
 * Drag handle between the sales list and the add-sale form — lets the user
 * resize how much vertical space the list gets versus the form below it.
 */
export const Splitter = styled.div<{ $dragging: boolean }>`
  flex-shrink: 0;
  height: 14px;
  margin: -7px 0;
  cursor: row-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  touch-action: none;

  &::after {
    content: "";
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: ${({ $dragging }) =>
      $dragging ? ColorV2.ElectricBlue : ColorV2.Blue5};
    transition: background 0.15s;
  }

  &:hover::after {
    background: ${ColorV2.ElectricBlue};
  }
`;

export const SaleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1.5px solid ${ColorV2.Blue6};
  border-radius: 10px;
  background: ${ColorV2.White};
`;

export const SaleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const SaleTitle = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SaleMeta = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

/** A non-interactive stand-in for the DatePicker, used when the date is
 * implied by the selected period (e.g. locked to the selected day). */
export const ReadOnlyDateLabel = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue3};
  background: ${ColorV2.Blue6};
  border: 1.5px solid ${ColorV2.Blue5};
  border-radius: 10px;
  padding: 8px 12px;
  box-sizing: border-box;
  white-space: nowrap;
`;

/**
 * Quick-fill grid used in week view — one row per weekday. Grows to fill
 * whatever vertical space is left in the pane (its parent AddSection is
 * stretched to flex:1 for week view) rather than sitting at a fixed height
 * with dead space below it; scrolls internally only if it still overflows.
 */
export const WeekGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px;
  border: 1.5px solid ${ColorV2.Blue6};
  border-radius: 10px;
`;

export const WeekRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const WeekDayLabel = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  min-width: 72px;
  flex-shrink: 0;
`;

/**
 * Shows the sum already recorded for that day, next to its amount field.
 * Clickable when there's a value to edit — clicking loads it into the
 * adjacent input so the user can adjust it instead of retyping from scratch.
 */
export const WeekCurrentLabel = styled.button<{ $clickable: boolean }>`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue3};
  /* Fixed (not min) width — a long amount like "1 000 000 kr" must not push
     the input/clear button further right than a short one or the dash. */
  width: 96px;
  flex-shrink: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: right;
  background: none;
  border: none;
  padding: 3px 6px;
  border-radius: 6px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: color 0.15s;

  &:hover {
    color: ${({ $clickable }) =>
      $clickable ? ColorV2.ElectricBlue : ColorV2.Blue3};
  }

  &:disabled {
    cursor: default;
  }
`;

export const WeekAmountField = styled.div`
  width: 120px;
  flex-shrink: 0;
`;

/** Fixed-width slot reserved on every week-grid row (not just rows with a
 * value) so the amount field lines up regardless of whether the clear
 * button is shown. */
export const ClearButtonSlot = styled.div`
  width: 24px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
`;

/** Opens the CSV import pane — styled to match the "Manage X" links used
 * elsewhere on the stat cards. */
export const ImportLink = styled.button`
  ${TypographyV2.s1}
  color: ${ColorV2.ElectricBlue};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  margin: -8px 24px 0;

  &:hover {
    text-decoration: underline;
  }
`;
